import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS} from "../presets/editing-styles";
import {FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * VERDADE DIFÍCIL #07 — "O Dono É o Gargalo"
 * Editing Style: CINEMATIC
 * Profile: @guilhermeribeiro.ai (foto real do Instagram)
 * Copy: BoardCopy (Halbert + Sugarman + Hormozi)
 * Audio: ElevenLabs TTS per-scene, minimal padding (15f)
 */

const AMBER = "#f5ad43";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const GREEN = "#00DF82";
const CYAN = "#42e9f5";
const RED = "#F87171";
const PURPLE = "#a78bfa";
const PURPLE_DARK = "#120520";

const SZ = SAFE_ZONES.universal;

// Audio timing — synced to ElevenLabs output + 15f minimal padding
const SCENES_CONFIG = [
  {dur: 110, file: "audio/verdade07/verdade07_c01.mp3", dir: "right" as const},
  {dur: 593, file: "audio/verdade07/verdade07_c02.mp3", dir: "left" as const},
  {dur: 338, file: "audio/verdade07/verdade07_c03.mp3", dir: "up" as const},
  {dur: 487, file: "audio/verdade07/verdade07_c04.mp3", dir: "right" as const},
  {dur: 204, file: "audio/verdade07/verdade07_c05.mp3", dir: "up" as const},
];

// ─── Ambient particles ────────────────────────────────────────────────
const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`px-${i}`) * 1080;
        const baseY = random(`py-${i}`) * 1920;
        const size = 2 + random(`ps-${i}`) * 3;
        const speed = 0.2 + random(`psp-${i}`) * 0.4;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.08 + random(`po-${i}`) * 0.12,
          }} />
        );
      })}
    </>
  );
};

// ─── Bottleneck funnel visual ─────────────────────────────────────────
const BottleneckFunnel: React.FC<{progress: number}> = ({progress}) => {
  const squeeze = interpolate(progress, [0, 0.5, 1], [1, 0.15, 0.15], CLAMP);
  const opacity = interpolate(progress, [0, 0.3], [0, 0.6], CLAMP);

  return (
    <div style={{
      position: "absolute", left: "50%", top: "50%",
      transform: "translate(-50%, -50%)",
      opacity,
    }}>
      {/* Top wide part */}
      <div style={{
        width: 600, height: 200,
        background: `linear-gradient(180deg, rgba(167,139,250,0.15), rgba(167,139,250,0.05))`,
        borderRadius: "12px 12px 0 0",
        borderTop: `1px solid rgba(167,139,250,0.2)`,
        borderLeft: `1px solid rgba(167,139,250,0.2)`,
        borderRight: `1px solid rgba(167,139,250,0.2)`,
      }} />
      {/* Squeeze point */}
      <div style={{
        width: 600, height: 80, position: "relative",
        overflow: "hidden",
      }}>
        <div style={{
          position: "absolute", left: "50%", top: 0,
          width: `${squeeze * 100}%`,
          transform: "translateX(-50%)",
          height: "100%",
          background: `linear-gradient(180deg, rgba(167,139,250,0.05), rgba(248,113,113,0.15))`,
          borderLeft: `1px solid rgba(248,113,113,0.3)`,
          borderRight: `1px solid rgba(248,113,113,0.3)`,
          borderRadius: "0 0 8px 8px",
        }} />
      </div>
    </div>
  );
};

// ─── Pressure meter bar ───────────────────────────────────────────────
const PressureBar: React.FC<{
  label: string; value: number; delay: number; color: string;
}> = ({label, value, delay, color}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.smooth});
  const fillW = interpolate(p, [0, 1], [0, value], CLAMP);

  if (frame < delay) return null;
  return (
    <div style={{opacity: p, width: "100%"}}>
      <div style={{
        fontSize: FONT_SCALE.label.min, fontWeight: 600,
        color: MUTED, fontFamily: FONT_STACK, marginBottom: 8,
      }}>{label}</div>
      <div style={{
        width: "100%", height: 18, borderRadius: 9,
        background: "rgba(255,255,255,0.06)",
        overflow: "hidden",
      }}>
        <div style={{
          width: `${fillW}%`, height: "100%", borderRadius: 9,
          background: `linear-gradient(90deg, ${color}, ${color}88)`,
          boxShadow: `0 0 20px ${color}40`,
        }} />
      </div>
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES_CONFIG[0].file)} volume={0.9} />
    <GradientText text="VERDADE #07" colors={[PURPLE, AMBER]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
  </AbsoluteFill>
);

// ─── SCENE 2: REVELAÇÃO — "O dono é o gargalo" ──────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  const titleP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const funnelP = interpolate(frame, [30, 180], [0, 1], CLAMP);
  const subP = spring({frame: frame - 200, fps, config: SPRINGS.smooth});
  const tagP = spring({frame: frame - 350, fps, config: SPRINGS.snappy});

  return (
    <OrganicLayer intensity="subtle" seed="verdade-07">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 30}}>
      <Audio src={staticFile(SCENES_CONFIG[1].file)} volume={0.9} />

      <BottleneckFunnel progress={funnelP} />

      <div style={{
        opacity: titleP,
        transform: `translateY(${interpolate(titleP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(titleP, [0, 1], [0.9, 1], CLAMP)})`,
        fontSize: FONT_SCALE.h1.min, fontWeight: 900, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.1,
        zIndex: 2,
      }}>
        O dono{"\n"}é o <span style={{color: RED}}>gargalo</span>.
      </div>

      <div style={{
        opacity: subP,
        transform: `translateY(${interpolate(subP, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.4,
        maxWidth: "88%", zIndex: 2,
      }}>
        A empresa cresce até o tamanho{"\n"}da capacidade de decisão do dono.
      </div>

      <div style={{
        opacity: tagP,
        transform: `scale(${interpolate(tagP, [0, 1], [0.8, 1], CLAMP)})`,
        padding: "14px 32px", borderRadius: 999,
        background: "rgba(248,113,113,0.12)",
        border: "1px solid rgba(248,113,113,0.25)",
        fontSize: FONT_SCALE.label.min, fontWeight: 700, color: RED,
        fontFamily: FONT_STACK, zIndex: 2,
      }}>
        E para aí.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: EVIDÊNCIA — sinais do gargalo ──────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const introP = spring({frame, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES_CONFIG[2].file)} volume={0.9} />
      <div style={{
        opacity: introP, fontSize: FONT_SCALE.body.min, fontWeight: 600,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", marginBottom: 8,
      }}>
        Sinais de que o gargalo é você:
      </div>
      <div style={{width: "85%", display: "flex", flexDirection: "column", gap: 22}}>
        <PressureBar label="Decisões acumuladas" value={95} delay={40} color={RED} />
        <PressureBar label="Equipe esperando aprovação" value={80} delay={80} color={AMBER} />
        <PressureBar label="Tarefas que 'só você sabe'" value={88} delay={120} color={PURPLE} />
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: VIRADA — sistema > centralização ──────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 60, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 160, fps, config: SPRINGS.bouncy});
  const p4 = spring({frame: frame - 280, fps, config: SPRINGS.snappy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES_CONFIG[3].file)} volume={0.9} />
      <div style={{
        opacity: p1, transform: `translateX(${interpolate(p1, [0, 1], [-40, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK,
      }}>
        Não é fazer menos.
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "92%",
      }}>
        É construir um sistema onde as coisas andam sem você aprovar cada vírgula.
      </div>
      <div style={{display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap", marginTop: 12}}>
        <GradientText text="Centralizar" colors={[RED, "#ff9999"]} fontSize={FONT_SCALE.h1.min} delay={140} springProfile="bouncy" />
        <div style={{opacity: p3, fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>→</div>
        <GradientText text="Sistematizar" colors={[GREEN, CYAN]} fontSize={FONT_SCALE.h1.min} delay={160} springProfile="bouncy" />
      </div>
      <div style={{
        opacity: p4, fontSize: FONT_SCALE.body.min, fontWeight: 800,
        color: AMBER, fontFamily: FONT_STACK, marginTop: 8,
      }}>
        Crescimento previsível começa aqui.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA com foto real ──────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 50, fps, config: SPRINGS.bouncy});
  const pulse = frame > 100 ? 1 + Math.sin((frame - 100) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES_CONFIG[4].file)} volume={0.9} />
      <div style={{
        opacity: profileP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
        transform: `translateY(${interpolate(profileP, [0, 1], [15, 0], CLAMP)}px)`,
      }}>
        Na próxima: o teste do "e se eu sumir 30 dias?"
      </div>
      <div style={{
        opacity: profileP,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
        display: "flex", alignItems: "center", gap: 16,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${PURPLE}`,
          boxShadow: `0 0 20px rgba(167,139,250,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>
            @guilhermeribeiro.ai
          </div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>
            Crescimento previsível
          </div>
        </div>
      </div>
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: PURPLE,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px rgba(167,139,250,0.4)`,
      }}>
        SALVA E SEGUE
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN COMPOSITION ─────────────────────────────────────────────────
export const VERDADE07_TOTAL = SCENES_CONFIG.reduce((s, sc) => s + sc.dur, 0);

export const Verdade07: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${PURPLE_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={30} color={PURPLE} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES_CONFIG.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
